import { vn } from 'vend-number'
import { LineItem } from '../models/line-item'
import { hasNonQuantityChange } from './continuation-line-item-comparison'

/**
 * Reconciles the virtual line item splits created for unfulfilled sale editing
 * back into the shape that reflects what the merchant actually changed.
 *
 * A split pair is a locked (fulfilled) half carrying
 * `continuationEditableLocalId`, and the editable (unfulfilled remainder) half
 * it names. Each pair is evaluated independently:
 *
 * - Nothing changed, only the quantity changed, or the editable half was
 *   deleted: the pair collapses back to a single line item under the original
 *   server ID.
 * - Anything else changed: the split is preserved as two ordinary line items.
 *
 * Both outcomes clear `continuationEditableLocalId`, so running this twice is a
 * no-op. That matters because an early-submitted sale is later finalised.
 */

export interface ReconcileContinuationSplitsOptions {
  /**
   * Forces every pair to the preserved-split outcome without comparing.
   *
   * Used when a sale recalculation is still pending, where a comparison could
   * be made against stale prices. Preserving is recoverable; collapsing is
   * not, since the server has no linkage between the halves with which to
   * detect a wrong one.
   */
  preserveAll?: boolean
}

/**
 * `vn()` throws on input it cannot parse rather than returning a non-finite
 * number. This runs on the sale completion path, so a bad quantity must not
 * escape as an exception.
 */
const parseQuantity = (value: LineItem['quantity']) => {
  try {
    return vn(value)
  } catch {
    return null
  }
}

/**
 * Returns a copy of the line item with the split markers removed.
 *
 * The destructuring names the two fields only so that `...rest` excludes them
 * — the `_` variables are never read. This omits the keys entirely rather than
 * setting them to `undefined`, and returns a new object rather than mutating
 * the input, which every caller relies on.
 */
const withoutSplitMarkers = (lineItem: LineItem): LineItem => {
  const {
    continuationEditableLocalId: _marker,
    isEditableForContinue: _editable,
    ...rest
  } = lineItem

  return rest
}

/**
 * Adds two quantity-scaled amounts, falling back to the locked half's value
 * for input VendNumber cannot parse, which it signals by throwing.
 *
 * Always returns a string. Every `LineItemTotals` field accepts one, and the
 * tax component fields are converted by their caller, so a single return type
 * keeps the assignments below checkable.
 */
const addAmounts = (a: string | number, b: string | number): string => {
  try {
    return vn(a).add(vn(b)).toString()
  } catch {
    return String(a)
  }
}

/**
 * Sums the totals of both halves.
 *
 * Every field on `LineItemTotals` is a line total rather than a per-unit
 * value, and a pair only collapses when the per-unit values are equal on both
 * halves, so the sum is exactly the total for the combined quantity.
 */
const mergeTotals = (
  locked: LineItem['totals'],
  editable: LineItem['totals']
): LineItem['totals'] => {
  const merged = { ...locked }

  Object.keys(merged).forEach(key => {
    const field = key as keyof typeof merged
    const lockedValue = merged[field]
    const editableValue = editable?.[field]

    if (lockedValue === undefined || editableValue === undefined) {
      return
    }

    merged[field] = addAmounts(lockedValue, editableValue)
  })

  return merged
}

/**
 * Sums the tax components of both halves, matched on `rate_id`.
 *
 * A rate present on only one half is carried across unchanged, which cannot
 * happen while the halves share a tax configuration but avoids silently
 * dropping tax if they ever diverge.
 */
const SUMMED_TAX_COMPONENT_FIELDS = [
  'total_tax',
  'base_total_tax',
  'total_tax_after_sale_discount',
] as const

const mergeTaxComponents = (
  locked: LineItem['taxComponents'],
  editable: LineItem['taxComponents']
): LineItem['taxComponents'] => {
  const remaining = [...(editable ?? [])]

  const merged = (locked ?? []).map(component => {
    const matchIndex = remaining.findIndex(
      candidate => candidate.rate_id === component.rate_id
    )

    if (matchIndex === -1) {
      return component
    }

    const [match] = remaining.splice(matchIndex, 1)
    const summed = { ...component }

    SUMMED_TAX_COMPONENT_FIELDS.forEach(field => {
      if (summed[field] === undefined || match[field] === undefined) {
        return
      }

      summed[field] = Number(addAmounts(summed[field], match[field]))
    })

    return summed
  })

  return [...merged, ...remaining]
}

/**
 * The collapsed line is the locked half carrying the combined quantity. It
 * keeps the original server ID and `confirmed: true`, and restores the
 * pre-split `fulfilmentCompleted`, which is always false because the split
 * refuses to split an already-completed line.
 *
 * The quantity-scaled totals are summed from both halves. Consumers derive
 * per-unit values by dividing a total by the quantity — `sale-transformer.ts`
 * computes `unit_tax` as `totals.tax / quantity` — so totals describing only
 * the locked half would understate the collapsed line.
 *
 * Note that `originalQuantity` stays at the full pre-split quantity while
 * `quantity` may now exceed it. That field only constrains returns, and a
 * continuation line is not a return, so it is inert.
 */
const collapse = (
  locked: LineItem,
  quantity: string,
  editable?: LineItem
): LineItem => ({
  ...withoutSplitMarkers(locked),
  quantity,
  displayQuantity: quantity,
  fulfilmentCompleted: false,
  confirmed: true,
  ...(editable
    ? {
        totals: mergeTotals(locked.totals, editable.totals),
        taxComponents: mergeTaxComponents(
          locked.taxComponents,
          editable.taxComponents
        ),
      }
    : {}),
})

export const reconcileContinuationSplits = (
  lineItems: LineItem[],
  { preserveAll = false }: ReconcileContinuationSplitsOptions = {}
): LineItem[] => {
  const byLocalId = new Map(lineItems.map(item => [item.localId, item]))
  const collapsedEditableLocalIds = new Set<string>()

  const reconciled = lineItems.map(lineItem => {
    const editableLocalId = lineItem.continuationEditableLocalId

    if (!editableLocalId) {
      return lineItem
    }

    if (preserveAll) {
      return withoutSplitMarkers(lineItem)
    }

    const editable = byLocalId.get(editableLocalId)
    const lockedQuantity = parseQuantity(lineItem.quantity)

    if (!lockedQuantity) {
      return withoutSplitMarkers(lineItem)
    }

    // The editable half was deleted: the line reverts to the fulfilled
    // quantity it already carries.
    if (!editable) {
      return collapse(lineItem, lockedQuantity.toString())
    }

    if (hasNonQuantityChange(lineItem, editable)) {
      return withoutSplitMarkers(lineItem)
    }

    const editableQuantity = parseQuantity(editable.quantity)

    if (!editableQuantity) {
      return withoutSplitMarkers(lineItem)
    }

    collapsedEditableLocalIds.add(editableLocalId)

    return collapse(
      lineItem,
      lockedQuantity.add(editableQuantity).toString(),
      editable
    )
  })

  return reconciled
    .filter(lineItem => !collapsedEditableLocalIds.has(lineItem.localId))
    .map(lineItem =>
      lineItem.isEditableForContinue ? withoutSplitMarkers(lineItem) : lineItem
    )
}
